import React, { useEffect, useState, useCallback } from 'react';
import { Tabs, Modal, message } from 'antd';
import {
  FastBackwardOutlined,
  LeftOutlined,
  RightOutlined,
  FastForwardOutlined,
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UndoOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useModulePermission } from '../../hooks/usePermission';
import { usePWT205Mode } from './hooks/usePWT205Mode';
import { usePWT205Record } from './hooks/usePWT205Record';
import { usePWT205Navigation } from './hooks/usePWT205Navigation';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Topics from './components/Tab2Topics';
import './PWT205Form.css';

const { TabPane } = Tabs;

const PWT205Form: React.FC = () => {
  const { mode, isEditing, enterAdd, enterEdit, enterBrowse } = usePWT205Mode();
  const permissions = useModulePermission('pwt205f1');

  const {
    record,
    formData,
    attendees,
    topics,
    trackings,
    validationErrors,
    loading,
    getField,
    setField,
    loadRecord,
    resetForAdd,
    revertToRecord,
    handleSave,
    handleDelete,
    addAttendeeRow,
    deleteAttendeeRow,
    updateAttendeeRow,
    addTopicRow,
    deleteTopicRow,
    updateTopicRow,
    addTrackingRow,
    deleteTrackingRow,
    updateTrackingRow,
  } = usePWT205Record(mode === 'add' ? enterBrowse : enterBrowse);

  const { allIds, currentIdx, loadList, navFirst, navLast, navPrev, navNext, searchAndNav } =
    usePWT205Navigation(loadRecord);

  const [searchKeyword, setSearchKeyword] = useState('');
  const [activeTab, setActiveTab] = useState('1');

  useEffect(() => {
    loadList('').then((ids) => {
      if (ids.length > 0) loadRecord(ids[0]);
    });
  }, []);

  const handleAdd = useCallback(() => {
    resetForAdd();
    enterAdd();
  }, [resetForAdd, enterAdd]);

  const handleEdit = useCallback(() => {
    enterEdit();
  }, [enterEdit]);

  const handleRevert = useCallback(() => {
    revertToRecord();
    enterBrowse();
  }, [revertToRecord, enterBrowse]);

  const handleSaveClick = useCallback(async () => {
    const isNew = mode === 'add';
    const result = await handleSave(isNew);
    if (result) {
      await loadList('');
    }
  }, [mode, handleSave, loadList]);

  const handleDeleteClick = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除會議記錄「${record.ft150_11}」嗎？`,
      okText: '確定刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const ok = await handleDelete(record.ft150_01);
        if (ok) {
          const ids = await loadList('');
          if (ids.length > 0) await loadRecord(ids[0]);
        }
      },
    });
  }, [record, handleDelete, loadList, loadRecord]);

  const handleSearch = useCallback(async () => {
    await searchAndNav(searchKeyword);
  }, [searchKeyword, searchAndNav]);

  const strField = (k: Parameters<typeof getField>[0]) => String(getField(k) ?? '');

  return (
    <div className="pwt205-form">
      {/* Header Info */}
      <div className="header-row">
        <div className="form-inline">
          <label>會議代號</label>
          <input value={strField('ft150_01')} readOnly disabled style={{ width: '120px' }} />
          <label>會議主題</label>
          <input
            value={strField('ft150_11')}
            onChange={(e) => setField('ft150_11', e.target.value)}
            disabled={!isEditing}
            maxLength={60}
            style={{ width: '300px' }}
          />
          <label>會議日期</label>
          <input
            type="date"
            value={strField('ft150_13').substring(0, 10)}
            onChange={(e) =>
              setField('ft150_13', e.target.value ? e.target.value + 'T00:00:00Z' : null)
            }
            disabled={!isEditing}
          />
          <label>開會通知單號</label>
          <input
            value={strField('ft150_17')}
            readOnly
            disabled
            style={{ width: '120px' }}
          />
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="validation-errors">
          {validationErrors.map((e, i) => (
            <div key={i} className="validation-error">
              {e.field}: {e.message}
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} size="small">
        <TabPane tab="基本資料" key="1">
          <Tab1BasicInfo
            formData={formData}
            attendees={attendees}
            isEditing={isEditing}
            getField={getField}
            setField={setField}
            onAddAttendee={addAttendeeRow}
            onDeleteAttendee={deleteAttendeeRow}
            onUpdateAttendee={updateAttendeeRow}
          />
        </TabPane>
        <TabPane tab="會議內容" key="2">
          <Tab2Topics
            topicCount={formData.ft150_19 as number ?? 0}
            topics={topics}
            trackings={trackings}
            isEditing={isEditing}
            onAddTopic={addTopicRow}
            onDeleteTopic={deleteTopicRow}
            onUpdateTopic={updateTopicRow}
            onAddTracking={addTrackingRow}
            onDeleteTracking={deleteTrackingRow}
            onUpdateTracking={updateTrackingRow}
          />
        </TabPane>
      </Tabs>

      {/* Action Bar */}
      <div className="action-bar">
        {/* Navigation */}
        <button onClick={navFirst} disabled={isEditing || currentIdx <= 0} title="最前">
          <FastBackwardOutlined />
        </button>
        <button onClick={navPrev} disabled={isEditing || currentIdx <= 0} title="上一筆">
          <LeftOutlined />
        </button>
        <button
          onClick={navNext}
          disabled={isEditing || currentIdx >= allIds.length - 1}
          title="下一筆"
        >
          <RightOutlined />
        </button>
        <button
          onClick={navLast}
          disabled={isEditing || currentIdx >= allIds.length - 1}
          title="最後"
        >
          <FastForwardOutlined />
        </button>

        <span className="divider" />

        {/* Search */}
        <div className="search-box">
          <input
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="搜尋..."
            disabled={isEditing}
          />
          <button onClick={handleSearch} disabled={isEditing} title="查詢">
            <SearchOutlined />
          </button>
        </div>

        <span className="divider" />

        {/* Add / Save */}
        {!isEditing ? (
          <button
            onClick={handleAdd}
            disabled={!permissions.canCreate}
            title="新增"
          >
            <PlusOutlined /> 新增
          </button>
        ) : (
          <button
            onClick={handleSaveClick}
            disabled={loading}
            title="存檔"
          >
            <SaveOutlined /> 存檔
          </button>
        )}

        {/* Edit / Revert */}
        {!isEditing ? (
          <button
            onClick={handleEdit}
            disabled={!record || !permissions.canEdit}
            title="修改"
          >
            <EditOutlined /> 修改
          </button>
        ) : (
          <button onClick={handleRevert} title="還原">
            <UndoOutlined /> 還原
          </button>
        )}

        {/* Delete */}
        <button
          onClick={handleDeleteClick}
          disabled={!record || isEditing || !permissions.canDelete}
          title="刪除"
        >
          <DeleteOutlined /> 刪除
        </button>

        <span className="divider" />

        {/* Leave */}
        <button
          onClick={() => {
            if (isEditing) {
              Modal.confirm({
                title: '確認離開',
                content: '編輯中的資料尚未儲存，確定離開嗎？',
                onOk: () => {
                  revertToRecord();
                  enterBrowse();
                },
              });
            } else {
              window.history.back();
            }
          }}
          title="離開"
        >
          <CloseOutlined /> 離開
        </button>

        {/* Record counter */}
        <span className="record-counter">
          {allIds.length > 0
            ? `第 ${currentIdx + 1} 筆 / 共 ${allIds.length} 筆`
            : '（無資料）'}
        </span>
      </div>
    </div>
  );
};

export default PWT205Form;
